import { CRM_CREATED_DATE_PERIODS } from "./crmCreatedDateFilters";

/**
 * Filter taxonomy v1.5 "Has changed to / Has changed from" for status and
 * health fields. A Has operator tests something that happened and carries its
 * own time window on the same row, so the value pairs the target state with a
 * rolling past window rather than adding a second date condition.
 */
export const CRM_STATE_CHANGE_CONDITIONS = [
  "has_changed_to",
  "has_changed_from",
] as const;
export type CrmStateChangeCondition =
  (typeof CRM_STATE_CHANGE_CONDITIONS)[number];

/**
 * Rolling past windows the Admin client offers and accepts today; only a past
 * window describes a change that has already happened.
 */
export const CRM_STATE_CHANGE_WINDOWS = CRM_CREATED_DATE_PERIODS.filter(
  (period) => period.hours < 0,
);
export type CrmStateChangeWindow = (typeof CRM_STATE_CHANGE_WINDOWS)[number];

export type CrmStateChangeWindowDefinition = {
  value: string;
  label: string;
  /** Hours from now to the lower bound; null for "Any time" (no bound). */
  hours: number | null;
};

/**
 * Every window the API accepts, evaluates and labels: the client's windows
 * plus "Any time" and "Last 14 days" from the Audiences spec. The client keeps
 * its own set until it adopts these.
 */
export const CRM_STATE_CHANGE_ACCEPTED_WINDOWS: readonly CrmStateChangeWindowDefinition[] =
  [
    { value: "any_time", label: "Any time", hours: null },
    ...CRM_STATE_CHANGE_WINDOWS,
    { value: "last_14_days", label: "Last 14 days", hours: -336 },
  ];

/**
 * The "When" options in the spec's order (Audiences modals row 15: Any time ·
 * Last 7 · 14 · 30 · 60 · 90 days; "Any time" is the default). "Last 48 hours"
 * stays accepted for saved filters but is not offered.
 */
export const CRM_STATE_CHANGE_WINDOW_OPTIONS: readonly CrmStateChangeWindowDefinition[] =
  [
    "any_time",
    "last_7_days",
    "last_14_days",
    "last_30_days",
    "last_60_days",
    "last_90_days",
  ].map((value) => {
    const window = CRM_STATE_CHANGE_ACCEPTED_WINDOWS.find(
      (entry) => entry.value === value,
    );
    if (!window) throw new Error(`Unknown state-change window: ${value}`);
    return window;
  });

export type CrmStateChangeValue = {
  /** Registry wire key of the state the record changed to or from. */
  value: string;
  /** Rolling past window identifier from CRM_STATE_CHANGE_WINDOWS. */
  within: CrmStateChangeWindow["value"];
};

export const isCrmStateChangeCondition = (
  condition: unknown,
): condition is CrmStateChangeCondition =>
  CRM_STATE_CHANGE_CONDITIONS.some((allowed) => allowed === condition);

const readValueWithin = <TWindow extends string>(
  value: unknown,
  windows: readonly { value: TWindow }[],
): { value: string; within: TWindow } | null => {
  let candidate: unknown = value;
  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    if (!trimmed.startsWith("{")) return null;
    try {
      candidate = JSON.parse(trimmed) as unknown;
    } catch {
      return null;
    }
  }
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate))
    return null;
  const { value: state, within } = candidate as Record<string, unknown>;
  const window = windows.find((entry) => entry.value === within);
  if (typeof state !== "string" || !window) return null;
  const trimmedState = state.trim();
  if (!trimmedState) return null;
  return { value: trimmedState, within: window.value };
};

/**
 * Reads a stored or submitted value without accepting unknown shapes, against
 * the client's windows (CRM_STATE_CHANGE_WINDOWS).
 */
export const readCrmStateChangeValue = (
  value: unknown,
): CrmStateChangeValue | null =>
  readValueWithin(value, CRM_STATE_CHANGE_WINDOWS);

const readAccepted = (value: unknown) =>
  readValueWithin(value, CRM_STATE_CHANGE_ACCEPTED_WINDOWS);

/**
 * Canonical stored form: a JSON string holding the state key and the window,
 * matching how calendar date drafts are stored. `allowedStates` are the wire
 * keys the owning registry accepts for the field; anything else is invalid
 * rather than silently matching nothing. The API accepts every window in
 * CRM_STATE_CHANGE_ACCEPTED_WINDOWS.
 */
export const normalizeCrmStateChangeValue = (
  condition: string,
  value: unknown,
  allowedStates: readonly string[],
): string | null => {
  if (!isCrmStateChangeCondition(condition)) return null;
  const parsed = readAccepted(value);
  if (!parsed || !allowedStates.includes(parsed.value)) return null;
  return JSON.stringify({ value: parsed.value, within: parsed.within });
};

export type CrmStateChangeConstraint = {
  direction: "to" | "from";
  state: string;
  /** Inclusive lower bound and inclusive upper bound, ISO instants. */
  lower: string;
  upper: string;
};

/**
 * A storage-neutral constraint for an EXISTS query over recorded state
 * changes, resolved against the list or evaluation's single clock.
 */
export const resolveCrmStateChangeConstraint = (
  condition: string,
  value: unknown,
  nowMs: number,
): CrmStateChangeConstraint | null => {
  if (!isCrmStateChangeCondition(condition) || !Number.isFinite(nowMs))
    return null;
  const parsed = readAccepted(value);
  if (!parsed) return null;
  const window = CRM_STATE_CHANGE_ACCEPTED_WINDOWS.find(
    (entry) => entry.value === parsed.within,
  );
  if (!window) return null;
  // "Any time" keeps the constraint shape with the earliest instant.
  const lower = new Date(
    window.hours === null ? 0 : nowMs + window.hours * 3_600_000,
  );
  const upper = new Date(nowMs);
  if (!Number.isFinite(lower.getTime())) return null;
  return {
    direction: condition === "has_changed_to" ? "to" : "from",
    state: parsed.value,
    lower: lower.toISOString(),
    upper: upper.toISOString(),
  };
};

/** Display form for saved filter chips; keeps the stored key untouched. */
export const formatCrmStateChangeValue = (
  value: unknown,
  labelForState: (state: string) => string | null | undefined,
): string | null => {
  const parsed = readAccepted(value);
  if (!parsed) return null;
  const window = CRM_STATE_CHANGE_ACCEPTED_WINDOWS.find(
    (entry) => entry.value === parsed.within,
  );
  const stateLabel = labelForState(parsed.value) ?? parsed.value;
  return window ? `${stateLabel} · ${window.label}` : stateLabel;
};
